/**
 * ArtifactTypePage — die Artefakte EINES Typs in EINEM Stand.
 *
 * Zwei Wege führen zum Stand, und ihre Rangfolge ist festgelegt (AP-B):
 * Trägt die Route ihn (`/artifacts/:type/:release`), gewinnt der Deep-Link und
 * **setzt die globale Wahl nach** (`useSyncReleaseFromRoute`). Ohne Stand in
 * der Route folgt die Seite der Kopfzeile — kennt der Typ den dort gewählten
 * Stand nicht, fällt sie auf ihren letzten befüllten Stand zurück, statt leer
 * zu laufen.
 */
import { Link, useParams } from "react-router-dom";
import { Card, Badge, Icon, Notice, Breadcrumbs, Breadcrumb } from "@conuti-das/prince-ui";
import { Flex, princeIcon } from "@/ui-prince";
import { useRelease, useSyncReleaseFromRoute } from "@/context";
import artifactsData from "@/data/generated/artifacts.json";
import runsData from "@/data/generated/runs.json";
import { ALL } from "@/types/pipeline";
import type { ArtifactIndex, Run } from "@/types";

const TYPE_LABEL: Record<string, string> = {
  ebds: "EBDs",
  ahb: "AHB",
  mig: "MIG",
  pruefidentifikatoren: "Prüfidentifikatoren",
  codelisten: "Codelisten",
};

/** Badge-Link that opens a viewer. */
function ViewerTag({ to, label, title }: { to: string; label: string; title?: string }) {
  return (
    <Link to={to} style={{ textDecoration: "none" }} title={title}>
      <Badge tone="teal">{label}</Badge>
    </Link>
  );
}

// ── Typ-spezifische Viewer-Listen ────────────────────────────────────────────

/** EBDs → bestehender EBD-Viewer (/ebd/:release/:code). */
function EbdItems({ data, release }: { data: ArtifactIndex; release: string }) {
  const codes = data.ebds[release] ?? [];
  const names = data.ebdNames?.[release] ?? {};
  if (codes.length === 0) return <EmptyHint type="EBDs" />;
  return (
    <Flex style={{ gap: "var(--s-1)", flexWrap: "wrap" }}>
      {codes.map((code) => (
        <ViewerTag
          key={code}
          to={`/ebd/${release}/${code}`}
          label={code}
          title={names[code] ?? code}
        />
      ))}
    </Flex>
  );
}

/** AHB → Regelwerk-Tabellen-Viewer, gruppiert nach Nachrichtentyp. */
function AhbItems({ data, release }: { data: ArtifactIndex; release: string }) {
  const byMsg = data.ahbs[release] ?? {};
  const msgTypes = Object.keys(byMsg).sort();
  if (msgTypes.length === 0) return <EmptyHint type="AHB-Prüfids" />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}>
      {msgTypes.map((msg) => (
        <Card
          key={msg}
          title={
            <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
              <Icon name={princeIcon("document")} size={16} />
              <span>{msg}</span>
            </Flex>
          }
          header={
            <span style={{ fontSize: "0.78rem", color: "var(--sapContent_LabelColor)" }}>
              {(byMsg[msg] ?? []).length} Prüfids
            </span>
          }
        >
          <Flex style={{ padding: "var(--s-3)", gap: "var(--s-1)", flexWrap: "wrap" }}>
            {(byMsg[msg] ?? []).map((pid) => (
              <ViewerTag
                key={pid}
                to={`/regelwerk/tabellen?mode=single&fv=${release}&doctype=ahb&format=${encodeURIComponent(msg)}&pruefid=${encodeURIComponent(pid)}`}
                label={pid}
                title={`${msg} · Prüfid ${pid} im Tabellen-Viewer öffnen`}
              />
            ))}
          </Flex>
        </Card>
      ))}
    </div>
  );
}

/** MIG → Regelwerk-Tabellen-Viewer (verschachtelte Segmentstruktur). */
function MigItems({ data, release }: { data: ArtifactIndex; release: string }) {
  const msgTypes = data.migs?.[release] ?? [];
  if (msgTypes.length === 0) return <EmptyHint type="MIGs" />;
  return (
    <Flex style={{ gap: "var(--s-2)", flexWrap: "wrap" }}>
      {msgTypes.map((msg) => (
        <Link
          key={msg}
          to={`/regelwerk/tabellen?mode=single&fv=${release}&doctype=mig&format=${encodeURIComponent(msg)}`}
          style={{ textDecoration: "none" }}
        >
          <Card
            title={
              <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
                <Icon name={princeIcon("source-code")} size={16} />
                <span>{msg}</span>
              </Flex>
            }
            style={{ width: "300px" }}
          >
            <div style={{ padding: "var(--s-2) var(--s-3)", fontSize: "0.78rem", opacity: 0.7 }}>
              Segmentstruktur im Tabellen-Viewer öffnen
            </div>
          </Card>
        </Link>
      ))}
    </Flex>
  );
}

/** Prüfidentifikatoren → eigener Tabellen-Viewer (/pruefid/:release). */
function PruefidItems({ data, release }: { data: ArtifactIndex; release: string }) {
  const sets = data.pruefidSets?.[release] ?? [];
  if (sets.length === 0) return <EmptyHint type="Prüfidentifikatoren-Tabellen" />;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}>
      {sets.map((set) => (
        <Card
          key={set.source}
          title={
            <Flex style={{ alignItems: "center", gap: "var(--s-2)" }}>
              <Icon name={princeIcon("inspect")} size={16} />
              <span>{set.source}</span>
            </Flex>
          }
          header={
            <span style={{ fontSize: "0.78rem", color: "var(--sapContent_LabelColor)" }}>
              {set.tables.length} Tabellen
            </span>
          }
        >
          <Flex style={{ padding: "var(--s-3)", gap: "var(--s-1)", flexWrap: "wrap" }}>
            {set.tables.map((t) => (
              <ViewerTag
                key={t.file}
                to={`/pruefid/${release}?source=${encodeURIComponent(set.source)}&table=${encodeURIComponent(t.file)}`}
                label={`${t.sheet} (${t.rows})`}
                title={`Tabelle „${t.sheet}" im Prüfid-Viewer öffnen`}
              />
            ))}
          </Flex>
        </Card>
      ))}
    </div>
  );
}

function EmptyHint({ type }: { type: string }) {
  return (
    <Notice tone="info">
      Für dieses Release sind noch keine {type} extrahiert.
    </Notice>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ArtifactTypePage() {
  const { type, release: routeRelease } = useParams<{ type: string; release?: string }>();
  // Deep-Link zuerst: die Route gewinnt und zieht die Kopfzeile nach.
  useSyncReleaseFromRoute(routeRelease);
  const { release: headerRelease } = useRelease();
  const data = artifactsData as unknown as ArtifactIndex;
  const runs = runsData as Run[];

  if (!type) return null;

  const counts = data.counts[type] || {};
  const releases = Object.keys(counts).sort();
  const withData = releases.filter((r) => counts[r] > 0);
  const fallbackRelease = withData[withData.length - 1] ?? releases[0];
  // Die Kopfzeile trägt nur, was dieser Typ überhaupt kennt: `ebds` hat kein
  // 202710, `schemas` nur 202610. Sonst zeigte die Seite einen Stand, den es
  // für sie nicht gibt.
  const fromHeader =
    headerRelease !== ALL && releases.includes(headerRelease) ? headerRelease : undefined;
  const activeRelease = routeRelease ?? fromHeader ?? fallbackRelease;
  const headerMiss = !routeRelease && headerRelease !== ALL && fromHeader === undefined;

  if (!activeRelease) {
    return (
      <div className="page">
        <h2 className="page-title">Keine Daten für „{type}"</h2>
      </div>
    );
  }

  const matchingRuns = runs.filter((r) => r.release === activeRelease);

  return (
    <div className="page">
      <Breadcrumbs>
        <Breadcrumb>
          <Link to="/artifacts" style={{ textDecoration: "none" }}>Artefakte</Link>
        </Breadcrumb>
        <Breadcrumb>
          <span>{TYPE_LABEL[type] ?? type}</span>
        </Breadcrumb>
        <Breadcrumb>
          <span>{activeRelease}</span>
        </Breadcrumb>
      </Breadcrumbs>

      <div className="page-header" style={{ marginTop: "var(--s-3)" }}>
        <h2 className="page-title">{TYPE_LABEL[type] ?? type.toUpperCase()} · Release {activeRelease}</h2>
        <div className="page-subtitle">
          {counts[activeRelease]} Artefakte extrahiert — klicke ein Artefakt, um es im Viewer zu öffnen.
        </div>
      </div>

      <Flex style={{ gap: "var(--s-2)", alignItems: "center", marginBottom: "var(--s-4)", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600 }}>Release wechseln:</span>
        {releases.map((r) => (
          <Link key={r} to={`/artifacts/${type}/${r}`} style={{ textDecoration: "none" }}>
            <Badge tone={r === activeRelease ? "blue" : "neutral"}>
              {r} ({counts[r] ?? 0})
            </Badge>
          </Link>
        ))}
      </Flex>

      {headerMiss && (
        <div style={{ marginBottom: "var(--s-4)" }}>
          <Notice tone="info">
            In der Kopfzeile ist Stand {headerRelease} gewählt — für{" "}
            {TYPE_LABEL[type] ?? type} gibt es diesen Stand nicht. Angezeigt wird{" "}
            {activeRelease}.
          </Notice>
        </div>
      )}

      {matchingRuns.length > 0 && (
        <Card
          title={`Runs für ${activeRelease}`}
          style={{ marginBottom: "var(--s-4)" }}
        >
          <Flex style={{ padding: "var(--s-3)", gap: "var(--s-2)", flexWrap: "wrap" }}>
            {matchingRuns.map((r) => (
              <Link key={r.id} to={`/runs/${r.id}`} style={{ textDecoration: "none" }}>
                <Badge tone="teal">{r.id}</Badge>
              </Link>
            ))}
          </Flex>
        </Card>
      )}

      {type === "ebds" && <EbdItems data={data} release={activeRelease} />}
      {type === "ahb" && <AhbItems data={data} release={activeRelease} />}
      {type === "mig" && <MigItems data={data} release={activeRelease} />}
      {type === "pruefidentifikatoren" && <PruefidItems data={data} release={activeRelease} />}
      {type === "codelisten" && (
        <Notice tone="info">
          Codelisten sind in der knowledge-collection noch nicht extrahiert — sobald
          Artefakte vorhanden sind, erscheint hier der Viewer.
        </Notice>
      )}
    </div>
  );
}
